import { createSignal, createEffect, untrack, For, Show } from "solid-js";
import { quantityBreakdown } from "../services/quantityFormatter";
import { formatInteger } from "../services/numberFormat";
import styles from "./QuantityInput.module.css";

const FILLER = /^(and|und|\+|,|x)$/i;

export function parseQuantityExpression(input: string, stackSize = 64): number | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;

  if (/^\d+$/.test(s)) return parseInt(s);

  const tokens = s
    .replace(/(\d)([a-z])/g, "$1 $2")
    .replace(/([a-z])(\d)/g, "$1 $2")
    .split(/[\s,+]+/)
    .filter(t => t.length > 0 && !FILLER.test(t));

  let total = 0;
  let i = 0;
  let matched = false;

  while (i < tokens.length) {
    const numStr = tokens[i];
    const num = parseFloat(numStr);

    if (isNaN(num)) {
      const unitMul = resolveUnit(numStr, stackSize);
      if (unitMul !== null) {
        total += unitMul;
        matched = true;
        i++;
        continue;
      }
      i++;
      continue;
    }

    if (i + 1 < tokens.length) {
      const unitMul = resolveUnit(tokens[i + 1], stackSize);
      if (unitMul !== null) {
        total += Math.floor(num * unitMul);
        matched = true;
        i += 2;
        continue;
      }
    }

    total += Math.floor(num);
    matched = true;
    i++;
  }

  return matched && total > 0 ? total : null;
}

function resolveUnit(token: string, stackSize: number): number | null {
  if (["s", "st", "stack", "stacks"].includes(token)) return stackSize;
  if (["sk", "sc", "sks", "scs", "chest", "chests"].includes(token)) return 27 * stackSize;
  if (["dk", "dc", "dks", "dcs", "doublechest", "doublechests"].includes(token)) return 54 * stackSize;
  return null;
}

interface Preset {
  label: string;
  value: number;
}

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  onSubmit?: (value: number) => void;
  presets?: Preset[] | false;
  min?: number;
  max?: number;
  stackSize?: number;
  placeholder?: string;
  class?: string;
  /** Single-row layout: input and presets sit side by side instead of stacking, for dense list rows. */
  inline?: boolean;
}

const defaultPresets = (stackSize: number): Preset[] => [
  { label: "1x", value: 1 },
  ...(stackSize > 1
    ? [{ label: "1 Stack", value: stackSize }, { label: "9 Stacks", value: 9 * stackSize }]
    : []),
  { label: "1 DK", value: 54 * stackSize },
];

export default function QuantityInput(props: QuantityInputProps) {
  const stackSize = () => props.stackSize ?? 64;
  const showPresets = () => props.presets !== false;
  const presetList = () => (Array.isArray(props.presets) ? props.presets : defaultPresets(stackSize()));
  const [rawInput, setRawInput] = createSignal(props.value > 0 ? String(props.value) : "");
  const [parsedValue, setParsedValue] = createSignal<number | null>(props.value || null);

  // parseQuantityExpression reads "0" as no quantity, so only a field whose floor is 0 accepts it.
  const allowsZero = () => props.min === 0;
  const parse = (raw: string) =>
    allowsZero() && /^\s*0+\s*$/.test(raw) ? 0 : parseQuantityExpression(raw, stackSize());

  createEffect(() => {
    const parsed = parse(rawInput());
    setParsedValue(parsed);
    if (parsed !== null && (parsed > 0 || allowsZero())) {
      const clamped = Math.max(
        props.min ?? 1,
        props.max ? Math.min(parsed, props.max) : parsed
      );
      // Untracked: a handler that reads the parent's state would otherwise re-run this effect on its own write.
      untrack(() => props.onChange(clamped));
    }
  });

  createEffect(() => {
    const incoming = props.value;
    untrack(() => {
      if (parse(rawInput()) === incoming) return;
      setRawInput(incoming > 0 || (incoming === 0 && allowsZero()) ? String(incoming) : "");
    });
  });

  function handlePreset(value: number) {
    setRawInput(String(value));
    props.onChange(value);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key !== "Enter") return;
    // Enter commits the quantity to this field; inside a form it would otherwise also submit the page.
    e.preventDefault();
    if (!props.onSubmit) return;
    const val = parsedValue();
    if (val && val > 0) {
      props.onSubmit(val);
      setRawInput("");
      setParsedValue(null);
    }
  }

  const inline = () => props.inline ?? false;

  return (
    <div class={`${styles.container} ${inline() ? styles.containerInline : ""} ${props.class ?? ""}`}>
      <div class={inline() ? styles.inputWrapInline : styles.inputWrap}>
        <input
          class={`${styles.input} ${inline() ? styles.inputInline : ""}`}
          type="text"
          placeholder={props.placeholder ?? "e.g. 2 stacks, 1dk 32..."}
          value={rawInput()}
          onInput={(e) => setRawInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
        />
        <Show when={!inline() && parsedValue() !== null && rawInput().length > 0}>
          <div class={styles.parsed}>
            <span class={styles.parsedValue}>= {formatInteger(parsedValue()!)}</span>
            <Show when={quantityBreakdown(parsedValue()!, stackSize())}>
              {(breakdown) => <span class={styles.parsedBreakdown}>({breakdown()})</span>}
            </Show>
          </div>
        </Show>
        <Show when={rawInput().length > 0 && parsedValue() === null}>
          <div class={`${styles.parsed} ${styles.parsedError}`}>
            <span>?</span>
          </div>
        </Show>
      </div>
      <Show when={showPresets()}>
        <div class={styles.presets}>
          <For each={presetList()}>
            {(preset) => (
              <button
                type="button"
                class={`${styles.presetBtn} ${parsedValue() === preset.value ? styles.presetActive : ""}`}
                onClick={() => handlePreset(preset.value)}
              >
                {preset.label}
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
